import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { css, keyframes } from "@nju/css";
import { IZone, useGroups } from "@nju/data-access-portal-dashboard";
import { IMemberOrchestrators } from "@nju/data-access-portal-member";
import {
  IMobileOffer,
  useOffer,
  useOfferMode,
  useOffers,
} from "@nju/data-access-portal-offers";
import { IPostpaidOffer } from "@nju/data-access-portal-offers/src/fetchProductOffers/types";
import {
  IMultiOrderOrchestrator,
  IOrchestrators,
} from "@nju/data-access-portal-orchestrator";
import { IMobileOrderProductsResponse } from "@nju/data-access-portal-product";
import { useMemberProducts } from "@nju/data-access-portal-product";
import { useMemberTasks, useTasks } from "@nju/data-access-portal-service";
import { IEsimQRCode } from "@nju/data-access-portal-sim-card";
import { MsisdnSwitcher } from "@nju/feature-portal-msisdn";
import { MobileStatusBanner, OrderCTABanner } from "@nju/feature-portal-order";
import { MobileOrderCompletedStatus } from "@nju/feature-portal-order";
import {
  MobileServiceCTABanner,
  MobileServiceDetailsCTABanner,
  PortoutServiceCTABanner,
} from "@nju/feature-portal-service";
import { portalRoutes } from "@nju/routes";
import { CircleLoader, Heading, Separator, Text } from "@nju/ui";
import { ChangeSimEsimBanner } from "../MySimCard/MySimCardPage/ChangeSimEsimBanner";
import {
  events,
  mobileDashboardMainPage,
} from "./mobileDashboardMainPage.machine";
import { isNextPaymentAfterPortout } from "./utils";

interface IMobileDashboardMainPageProps {
  handleSetSelectedNumber: (userId: string) => void;
  handleSetMultiDuoTrioContext: (flow: "multiOrder") => void;
  selectedMobileProducts: IMobileOrderProductsResponse;
  isLoading: boolean;
  multiOrderOrchestrator: IOrchestrators["multiOrder"] | undefined;
  selectedOrchestrators: IOrchestrators | IMemberOrchestrators;
  subState: IEsimQRCode["subState"] | undefined;
  zones: IZone[] | undefined;
}

export function MobileDashboardMainPage({
  handleSetSelectedNumber,
  handleSetMultiDuoTrioContext,
  selectedMobileProducts,
  isLoading,
  selectedOrchestrators,
  multiOrderOrchestrator,
  subState,
  zones,
}: IMobileDashboardMainPageProps) {
  const parentTasks = useTasks();
  const memberTasks = useMemberTasks();
  const groups = useGroups();
  const router = useRouter();
  const params = portalRoutes.mojeKonto.index.input(router.query).unwrap();
  const { mobileProducts } = useMemberProducts();
  const mode = useOfferMode();
  const { mobile: mobileOffers, postpaid: postpaidOffers } = useOffers();
  const offers = mode === "postpaid" ? postpaidOffers : mobileOffers;
  const offer = offers.indefiniteOffers.find((offer) => {
    return offer.display;
  });
  const currentOfferCategory: IMultiOrderOrchestrator["offerCategory"] =
    mode === "postpaid" ? "MOBILE_POSTPAID" : "MOBILE";
  const mobileOrchestratorByMode =
    (mode === "mobile" &&
      selectedOrchestrators?.mobile &&
      !selectedOrchestrators.mobile.offerCategory) ||
    selectedOrchestrators?.mobile?.offerCategory === currentOfferCategory
      ? selectedOrchestrators?.mobile
      : undefined;
  const multiOrderOrchestratorByMode =
    (mode === "mobile" &&
      multiOrderOrchestrator &&
      !multiOrderOrchestrator.offerCategory) ||
    multiOrderOrchestrator?.offerCategory === currentOfferCategory
      ? multiOrderOrchestrator
      : undefined;
  const hasGroupProduct = groups?.some((group) => {
    return group.members?.some((member) => {
      return (
        member.id === params?.userId &&
        member.primaryMsisdn &&
        (member.offerCategory === currentOfferCategory ||
          (mode === "mobile" && !member.offerCategory))
      );
    });
  });
  const offerId =
    mobileOrchestratorByMode?.offerId || multiOrderOrchestratorByMode?.offerId;
  const mobileOffer = useOffer(offerId) as
    | IMobileOffer
    | IPostpaidOffer
    | undefined;
  const { mnpPortout } = memberTasks || parentTasks;
  const showMultiOrderOnboardingBanner = params?.flow === "multiOrder";
  const simNumber = mobileProducts.simCard?.characteristic?.sim;
  const isESim =
    mobileProducts.simCard?.characteristic?.deliveryType === "ESIM";

  const [_, send] = useMachine(mobileDashboardMainPage, {
    actions: {
      onNewOrderSuccess: () => {
        router.push(portalRoutes.wybierak.get({ offerId: offer?.offerId }));
      },
      onAnotherOrderInProgress: () => {
        router.push(portalRoutes.zamowienieWTrakcie.get());
      },
    },
  });

  const mobileOrchestratorState = mobileOrchestratorByMode?.state;
  const showOrderStatusText =
    !hasGroupProduct &&
    mobileOrchestratorState !== "START" &&
    mobileOrchestratorState !== "COMPLETED";
  const showMobileOrderCompletedStatus =
    selectedMobileProducts?.hasProducts &&
    selectedMobileProducts?.mobileAccess?.status !== "PendingCancellation" &&
    selectedMobileProducts?.mobileAccess?.status !== "PendingTermination" &&
    selectedMobileProducts?.mobileAccess?.status !== "Suspended" &&
    selectedMobileProducts?.mobileAccess?.status !== "Disconnected";
  const isSuspendedOrDisconnected =
    selectedMobileProducts?.mobileAccess?.status === "Suspended" ||
    selectedMobileProducts?.mobileAccess?.status === "Disconnected";
  const showNextPayment = isNextPaymentAfterPortout(
    selectedMobileProducts?.mobileAccess?.anniversaryDate,
    mnpPortout?.properties?.npDueDate,
  );
  const clientNumber =
    selectedMobileProducts?.mobileAccess?.characteristic?.msisdn;
  const simId = mobileProducts.simCard?.id;

  const handleNewOrder = React.useCallback(() => {
    send({ type: events.NEW_ORDER });
  }, [send]);

  return (
    <>
      <Text
        size={{
          "@onlyMobile": "m",
          "@fromDesktop": "xl",
        }}
        weight="bold"
      >
        Twoje numery
      </Text>
      <Separator size="xs" tone="invisible" />
      <div className="only-mobile:pb-1">
        <MsisdnSwitcher
          context="DASHBOARD"
          flow={params?.flow}
          groups={groups}
          handleNewOrder={handleNewOrder}
          handleSetMultiDuoTrioContext={handleSetMultiDuoTrioContext}
          handleSetSelectedNumber={handleSetSelectedNumber}
          isCompleted={false}
          isLoading={isLoading}
          multiOrderOrchestrator={multiOrderOrchestratorByMode}
          orchestrator={mobileOrchestratorByMode}
        />
      </div>
      {isLoading && (
        <div className={loaderWrapper()}>
          <CircleLoader />
        </div>
      )}
      {!isLoading && (
        <>
          {!showMultiOrderOnboardingBanner && (
            <>
              {showOrderStatusText && (
                <>
                  <Heading
                    font="ttfirs"
                    tone="neutral"
                    variant="h4"
                    weight="bold"
                  >
                    <Text
                      size={{
                        "@onlyMobile": "m",
                        "@fromDesktop": "xl",
                      }}
                      weight="bold"
                    >
                      status Twojego zamówienia:
                    </Text>
                  </Heading>
                  <Separator size="s" tone="invisible" />
                </>
              )}
              {subState !== "Downloaded" && isESim && (
                <ChangeSimEsimBanner
                  changeSimEsimOrchestrator={
                    selectedOrchestrators.changeSimEsim
                  }
                  mobileOrchestrator={mobileOrchestratorByMode}
                  simCardProduct={mobileProducts.simCard}
                  simId={simId}
                  simNumber={simNumber}
                  subState={subState}
                  userId={params?.userId}
                />
              )}
              {offerId && (
                <MobileServiceCTABanner
                  offerId={offerId}
                  orchestrator={mobileOrchestratorByMode}
                  reactivationOrchestratorState={
                    selectedOrchestrators?.mobileReactivation?.state
                  }
                  serviceStatus={selectedMobileProducts?.mobileAccess?.status}
                />
              )}
              <PortoutServiceCTABanner
                orchestrator={mobileOrchestratorByMode}
                portoutData={mnpPortout?.properties}
              />
              {!hasGroupProduct && mobileOrchestratorByMode && (
                <MobileStatusBanner
                  flow={params?.flow}
                  orchestrator={mobileOrchestratorByMode}
                />
              )}
              {selectedMobileProducts && showMobileOrderCompletedStatus && (
                <MobileOrderCompletedStatus
                  clientNumber={clientNumber}
                  dataPackage={selectedMobileProducts?.dataPackage}
                  mobileAccess={selectedMobileProducts?.mobileAccess}
                  orchestrator={mobileOrchestratorByMode}
                  portoutInProgress={showNextPayment}
                  televisionProducts={selectedMobileProducts.television}
                  tvOrchestrator={selectedOrchestrators?.tv}
                  voicePackage={selectedMobileProducts?.voicePackage}
                  zones={zones}
                />
              )}
              {isSuspendedOrDisconnected && (
                <MobileServiceDetailsCTABanner
                  clientNumber={clientNumber}
                  discountFeatures={mobileOffer?.discountFeatures}
                  extraShortFeatures={mobileOffer?.extraShortFeatures}
                  isInactive={isSuspendedOrDisconnected}
                  paymentFeatures={mobileOffer?.paymentFeatures}
                  shortFeatures={mobileOffer?.shortFeatures}
                />
              )}
            </>
          )}
          {showMultiOrderOnboardingBanner && multiOrderOrchestratorByMode && (
            <OrderCTABanner
              flow={params.flow}
              orchestrator={multiOrderOrchestratorByMode}
            />
          )}
        </>
      )}
    </>
  );
}

const show = keyframes({
  "0%": { opacity: 0 },
  "1%": { opacity: 0 },
  "100%": { opacity: 1 },
});

const loaderWrapper = css({
  opacity: 0,
  display: "flex",
  pb: "$40",
  alignItems: "center",
  justifyContent: "center",
  animationName: `${show}`,
  animationDelay: "0.3s",
  animationDuration: "1s",
  animationFillMode: "forwards",
});
